import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getClientConsumptionSummary } from '../../utils/consumptionCalculations.js';
import { summarizeSolutions, transformSolutions } from '../../utils/solutionFunctions.js';
import WaterUsageChart from '../dashboardComponents/WaterUsageChart.js';
import { FaPlusCircle, FaMinusCircle } from "react-icons/fa"; // Assure-toi d'avoir installé react-icons
import WalterLoader from '../shared/WalterLoader';

import '../../styles/ClientDashboard.css';

const ClientActionPlan = () => {
  const { client_id, building_id } = useParams();
  const navigate = useNavigate();
  const [dataClient, setDataClient] = useState(null);
  const [dataBuilding, setDataBuilding] = useState(null)

  const [dataConsumptionCurrent, setDataConsumptionCurrent] = useState(null);
  const [dataEquipmentCurrent, setDataEquipmentCurrent] = useState(null);
  const [dataUsageCurrent, setDataUsageCurrent] = useState(null);

  const [dataConsumptionWithActions, setDataConsumptionWithActions] = useState(null);
  const [dataEquipmentWithActions, setDataEquipmentWithActions] = useState(null);
  const [dataUsageWithActions, setDataUsageWithActions] = useState(null);

  const [dataSolutions, setDataSolutions] = useState(null);
  const [dataSolutionsEquipments, setDataSolutionsEquipments] = useState(null);
  const [dataSolutionsUsages, setDataSolutionsUsages] = useState(null);

  const [activeTab, setActiveTab] = useState("Usages");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedActionEquipment, setSelectedActionEquipment] = useState({});
  const [selectedActionUsage, setSelectedActionUsage] = useState({});
  const [selectedUsage, setSelectedUsage] = useState({});

  const [listChosenActions, setListChosenActions] = useState({});
  const [hasExecutedOnce, setHasExecutedOnce] = useState(false);

  const [selectedFilter, setSelectedFilter] = useState('functional_unit');

  const [selectedGlobalSolution, setSelectedGlobalSolution] = useState(""); // dropdown global
  const [selectAllEligible, setSelectAllEligible] = useState(false); // checkbox "tout sélectionner"
  const [selectedEquipments, setSelectedEquipments] = useState([]); // équipements cochés individuellement

  // solutionsList = liste de toutes les solutions disponibles (à définir ou récupérer depuis tes données)
  const solutionsList = dataSolutionsEquipments?.flatMap(e => e.solutions || []) || [];

  // Fonction pour cocher / décocher tous les équipements éligibles
  const toggleSelectAll = (checked) => {
    setSelectAllEligible(checked);
    if (checked) {
      const allIds = dataSolutionsEquipments?.map(e => e.equipment_id) || [];
      setSelectedEquipments(allIds);
    } else {
      setSelectedEquipments([]);
    }
  };

  // Fonction pour cocher / décocher un équipement individuellement
  const toggleEquipmentSelection = (equipmentId, checked) => {
    setSelectedEquipments(prev => {
      if (checked) {
        return [...prev, equipmentId];
      } else {
        return prev.filter(id => id !== equipmentId);
      }
    });
  };

  // Fonction pour appliquer la solution globale à tous les équipements sélectionnés
  const applySolutionToSelected = () => {
    selectedEquipments.forEach(equipId => {
      const equip = dataSolutionsEquipments.find(e => e.equipment_id === equipId);
      if (equip) {
        const solution = equip.solutions?.find(s => s.solution_id === selectedGlobalSolution);
        if (solution) {
          toggleActionEquipment({ ...equip, ...solution });
        }
      }
    });
  };



  const totalPrice = Object.values(listChosenActions).reduce((sum, action) => {
    return sum + (action.data.price || 0);
  }, 0);

  const translations = {
    wc: "WC",
    urinal: "Urinoirs",
    faucet: "Lavabo/Evier",
    shower: "Douche",
    machine: "Machine",
    washing_machine: "Machine à laver",
    dishwasher: "Lave-vaisselle",
    machine_tap: "Robinet machine",
    cooling: "Circuit de refroidissement",
    all: "Tous"
};

  // Fonction pour gérer les changements de séléction d'action/usage
  const handleActionChangeEquipment = (equipmentId, action) => {
    setSelectedActionEquipment(prevState => ({
      ...prevState,
      [equipmentId]: action
    }));
  };

  const handleActionChangeUsage = (equipmentId, action) => {
    setSelectedActionUsage(prevState => ({
      ...prevState,
      [equipmentId]: action
    }));
  };

  const handleUsageChange = (equipmentId, action) => {
    setSelectedUsage(prevState => ({
      ...prevState,
      [equipmentId]: action
    }));
  };

  const handleFilterChange = (event) => {
    setSelectedFilter(event.target.value);
  };

  // Fonctions d'incrémentation et décrémentation pour les fréquences d'utilisation
  const handleIncrement = (dataUsage, equipmentId, field) => {
    setDataUsageWithActions((prevData) => {
      return prevData.map((equip) => {
        if (equip._id_equipement === equipmentId && equip.type_usage === selectedUsage[equipmentId]) {
          const incrementValue = equip.usage_data[field] >= 100 ? 10 : 1;
          return {
            ...equip,
            usage_data: {
              ...equip.usage_data,
              [field]: equip.usage_data[field] + incrementValue,
            },
          };
        }
        return equip;
      });
    });
  
    setListChosenActions((prevActions) => {
      const incrementValue = dataUsage.usage_data[field] >= 100 ? 10 : 1;
  
      return {
        ...prevActions,
        [dataUsage._id]: { 
          "building_id": building_id,
          "name_solution": "Modification de fréquence",
          "type_solution": "frequency",
          "id_equipement": dataUsage._id_equipement,
          "data": {
            "id_usage": dataUsage._id,
            "name_usage": dataUsage.type_usage,
            "daily_usage": field === "daily_usage" 
              ? dataUsage.usage_data.daily_usage + incrementValue
              : dataUsage.usage_data.daily_usage,
            "weekly_usage": field === "weekly_usage" 
              ? dataUsage.usage_data.weekly_usage + incrementValue
              : dataUsage.usage_data.weekly_usage,
            "yearly_usage": field === "yearly_usage" 
              ? dataUsage.usage_data.yearly_usage + incrementValue
              : dataUsage.usage_data.yearly_usage,
            "price": 0,
            "scope_of_works": "Faible",
            "water_savings": "unknown",
            "water_savings_unit": "unknown",
            "water_savings_m3": "None",
            "equipment_type": dataUsage.equipment_type,
            "machine_type": dataUsage.machine_type || null
          }
        }
      };
    });
  };
  
  const handleDecrement = (dataUsage, equipmentId, field) => {
    setDataUsageWithActions((prevData) => {
      return prevData.map((equip) => {
        if (equip._id_equipement === equipmentId && equip.type_usage === selectedUsage[equipmentId]) {
          const decrementValue = equip.usage_data[field] > 100 ? 10 : 1;
          return {
            ...equip,
            usage_data: {
              ...equip.usage_data,
              [field]: Math.max(equip.usage_data[field] - decrementValue, 0),
            },
          };
        }
        return equip;
      });
    });
  
    setListChosenActions((prevActions) => {
      const decrementValue = dataUsage.usage_data[field] > 100 ? 10 : 1;
  
      return {
        ...prevActions,
        [dataUsage._id]: {
          "building_id": building_id,
          "name_solution": "Modification de fréquence",
          "type_solution": "frequency",
          "id_equipement": dataUsage._id_equipement,
          "data": {
            "id_usage": dataUsage._id,
            "name_usage": dataUsage.type_usage,
            "daily_usage": field === "daily_usage" 
              ? Math.max(dataUsage.usage_data.daily_usage - decrementValue, 0) 
              : dataUsage.usage_data.daily_usage,
            "weekly_usage": field === "weekly_usage" 
              ? Math.max(dataUsage.usage_data.weekly_usage - decrementValue, 0) 
              : dataUsage.usage_data.weekly_usage,
            "yearly_usage": field === "yearly_usage" 
              ? Math.max(dataUsage.usage_data.yearly_usage - decrementValue, 0) 
              : dataUsage.usage_data.yearly_usage,
          "price": 0,
          "scope_of_works": "Faible",
          "water_savings": "unknown",
          "water_savings_unit": "unknown",
          "water_savings_m3": "None",
          "equipment_type": dataUsage.equipment_type,
          "machine_type": dataUsage.machine_type || null
        }
      }
    };
  });
  };
  
  // Fonctions de transformation de données pour les graphes donuts
  const aggregateUsageVolumes = (data) => {
    const usageTotals = {};
    if (data?.equipments) {
      Object.values(data.equipments).forEach((usages) => {
        Object.entries(usages).forEach(([usage, details]) => {
          const annualVolume = details.annual_volume || 0;
          usageTotals[usage] = (usageTotals[usage] || 0) + annualVolume;
        });
      });
    }
    return usageTotals;
  };

  // Résout l'id d'unité fonctionnelle stocké sur un usage en un nom lisible. Regroupe sous
  // "Non catégorisé" les cas où rien n'a jamais été assigné, la valeur legacy 'unknown', ou
  // un id qui ne correspond plus à aucune unité du bâtiment (unité supprimée depuis).
  const resolveFunctionalUnitLabel = (functionalUnitId) => {
    const match = (dataBuilding?.functional_units || []).find((fu) => fu.id === functionalUnitId);
    return match ? (match.name || "Non catégorisé") : "Non catégorisé";
  };

  const aggregateVolumeByFunctionalUnit = (data) => {
    const functionalUnitTotals = {};
    if (data?.equipments) {
      Object.values(data.equipments).forEach((usages) => {
        Object.values(usages).forEach((details) => {
          const label = resolveFunctionalUnitLabel(details.functional_unit);
          const annualVolume = details.annual_volume || 0;
          functionalUnitTotals[label] = (functionalUnitTotals[label] || 0) + annualVolume;
        });
      });
    }
    return functionalUnitTotals;
  };

  // Fonctions pour appliquer ou désappliquer une solution
  const applySolutionEquipment = (equipmentsDataset, equipmentId, waterSavings, waterSavingsUnit) => {
    for (let equipment of equipmentsDataset) {
      if (equipment['_id'] === equipmentId) { // Trouver l'équipement à modifier
        if (waterSavingsUnit === "%") {
          equipment['water_value'] *= (1 - waterSavings / 100); // Appliquer les économies en pourcentage
        } else if (waterSavingsUnit === equipment['water_value_unit']) {
          equipment['water_value'] -= waterSavings; // Appliquer les économies en valeur fixe
        }
      }
    }
  };

  const unapplySolutionEquipment = (equipmentsDataset, equipmentsDatasetOriginal, equipmentId) => {
    let originalValue = null;
    
    // Trouver la valeur originale de l'équipement
    for (let oEquipment of equipmentsDatasetOriginal) {
      if (oEquipment['_id'] === equipmentId) {
        originalValue = oEquipment['water_value'];
      }
    }
  
    // Restaurer la valeur originale de l'équipement
    for (let nEquipment of equipmentsDataset) {
      if (nEquipment['_id'] === equipmentId) {
        nEquipment['water_value'] = originalValue;
      }
    }
  };
  
  // Fonction des boutons toggle
  const toggleActionEquipment = (selectedEquipment, water_savings_m3=NaN) => {
    setDataEquipmentWithActions((prevData) => {
      const updatedData = [...prevData]; // Créer une copie du tableau

      const isActive = !selectedEquipment.is_solution_active;
      selectedEquipment.is_solution_active = isActive

      if (isActive) {
        // Appliquer la solution pour l'équipement
        if (selectedEquipment.equipment_id) {
          applySolutionEquipment(updatedData, selectedEquipment.equipment_id, selectedEquipment.water_savings, selectedEquipment.water_savings_unit);
          setListChosenActions((prevActions) => ({
            ...prevActions,
            [selectedEquipment.solution_id]: { 
              "building_id" : building_id,
              "name_solution" : selectedEquipment.solution_name,
              "type_solution" : "equipment",
              "id_equipement" : selectedEquipment.equipment_id,
              "data" : {
                "price": selectedEquipment.price,
                "scope_of_works": selectedEquipment.scope_of_works,
                "water_savings" : selectedEquipment.water_savings,
                "water_savings_unit" : selectedEquipment.water_savings_unit,
                "water_savings_m3" : isNaN(water_savings_m3) ? "None" : water_savings_m3,
                "equipment_type": selectedEquipment.equipment_type,
                "machine_type": selectedEquipment.machine_type || null
              }},
          }));
        }
      } else {
        // Annuler la solution pour l'équipement
        if (selectedEquipment.equipment_id) {
          unapplySolutionEquipment(updatedData, dataEquipmentCurrent, selectedEquipment.equipment_id);
          setListChosenActions((prevActions) => {
            const updatedActions = { ...prevActions };
            delete updatedActions[selectedEquipment.solution_id];
            return updatedActions;
          });
        }
      }

      return updatedData;
    });
  }

  const toggleActionUsage = (selectedUsage, water_savings_m3 = NaN) => {
    setDataEquipmentWithActions((prevData) => {
      const updatedData = [...prevData]; // Créer une copie du tableau

      const isActive = !selectedUsage.is_solution_active;
      selectedUsage.is_solution_active = isActive

      if (isActive) {
        // Appliquer la solution pour l'usage
        if (selectedUsage.equipment_id) {
          applySolutionEquipment(updatedData, selectedUsage.equipment_id, selectedUsage.water_savings, selectedUsage.water_savings_unit);
          setListChosenActions((prevActions) => ({
            ...prevActions,
            [selectedUsage.solution_id]: {
              "building_id" : building_id,
              "name_solution" : selectedUsage.solution_name,
              "type_solution" : "usage",
              "id_equipement" : selectedUsage.equipment_id,
              "data" : {
                "id_usage": selectedUsage.usage_id,
                "name_usage": selectedUsage.usage_type,
                "price": selectedUsage.price,
                "scope_of_works": selectedUsage.scope_of_works,
                "water_savings" : selectedUsage.water_savings,
                "water_savings_unit" : selectedUsage.water_savings_unit,
                "water_savings_m3" : isNaN(water_savings_m3) ? "None" : water_savings_m3,
                "equipment_type": selectedUsage.equipment_type,
                "machine_type": selectedUsage.machine_type || null
              }},
          }));
        }
      } else {
        // Annuler la solution pour l'équipement
        if (selectedUsage.equipment_id) {
          unapplySolutionEquipment(updatedData, dataEquipmentCurrent, selectedUsage.equipment_id);
          setListChosenActions((prevActions) => {
            const updatedActions = { ...prevActions };
            delete updatedActions[selectedUsage.solution_id];
            return updatedActions;
          });
        }
      }

      return updatedData;
    });
  };

  // Submit function pour le stockage des solutions choisies
  const handleSubmitActions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/save_solutions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(listChosenActions),
      });
  
      if (!response.ok) {
        throw new Error('Erreur lors de la sauvegarde des solutions');
      }
  
      const data = await response.json();
      alert('Solutions sauvegardées avec succès !');
    } catch (error) {
      console.log('Erreur API:', error);
      alert('Une erreur est survenue, veuillez réessayer.');
    }
  };

  // Récupération des données clients
  useEffect(() => {
    if (client_id) {
    fetch(`${process.env.REACT_APP_API_URL}/get_client_data_by_id/${client_id}`)
        .then((response) => response.json())
        .then((data) => {
        setDataClient(data);
        })
        .catch((err) => setError(err.message));
    }
  }, [client_id]);

  // Récupérer les infos sur le bâtiment
  useEffect(() => {
      if (building_id) {
        fetch(`${process.env.REACT_APP_API_URL}/get_building/${building_id}`)
          .then((response) => response.json())
          .then((data) => {
            setDataBuilding(data);
          })
          .catch((err) => setError(err.message));
      }
    }, [building_id]);

  // Récupération des équipements du client
  useEffect(() => {
    if (building_id) {
    fetch(`${process.env.REACT_APP_API_URL}/load_equipments/${building_id}`)
        .then((response) => response.json())
        .then((data) => {
        setDataEquipmentCurrent(data);
        setDataEquipmentWithActions(JSON.parse(JSON.stringify(data))); // afin d'éviter que les deux jeux de données soient liés
        })
        .catch((err) => setError(err.message));
    }
  }, [building_id]);

  // Récupération des usages du client
  useEffect(() => {
    if (dataEquipmentCurrent) {
    fetch(`${process.env.REACT_APP_API_URL}/load_usages_by_equipments/`, {
        method: "POST",
        headers: {
        "Content-Type": "application/json",
        },
        body: JSON.stringify(dataEquipmentCurrent),
    })
        .then((response) => {
        if (!response.ok) {
            throw new Error("Erreur lors de la récupération des usages.");
        }
        return response.json();
        })
        .then((data) => {
        setDataUsageCurrent(data);
        setDataUsageWithActions(JSON.parse(JSON.stringify(data)));
        setLoading(false);
        })
        .catch((err) => setError(err.message));
    }
  }, [dataEquipmentCurrent]);
  
  // Récupération des solutions
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/load_solutions/`)
      .then((response) => response.json())
      .then((data) => setDataSolutions(data))
      .catch((err) => setError(err.message));
  }, []);

  // Calcul des données nécéssaires aux graphiques
  useEffect(() => {
    if (dataClient && dataEquipmentCurrent && dataUsageCurrent) {
      const calculatedConsumption = getClientConsumptionSummary(dataClient, dataEquipmentCurrent, dataUsageCurrent);
        setDataConsumptionCurrent(calculatedConsumption);
      }
    }, [dataClient, dataEquipmentCurrent, dataUsageCurrent]);
  useEffect(() => {
    if (dataClient && dataEquipmentWithActions && dataUsageWithActions) {   
      const calculatedConsumption = getClientConsumptionSummary(dataClient, dataEquipmentWithActions, dataUsageWithActions);
        setDataConsumptionWithActions(calculatedConsumption);
      }
    }, [dataClient, dataEquipmentWithActions, dataUsageWithActions]);

  // Calcul des solutions (produit cartésien)
  useEffect(() => {
    if (dataEquipmentCurrent && dataUsageCurrent && dataSolutions) {
      const dataSolutionsGeneral = transformSolutions(summarizeSolutions(dataEquipmentCurrent, dataUsageCurrent, dataSolutions))
      setDataSolutionsEquipments(dataSolutionsGeneral['equipments']);
      setDataSolutionsUsages(dataSolutionsGeneral['usages']);
    }

  }, [dataEquipmentCurrent, dataUsageCurrent, dataSolutions]);
  
  // Récupération des solutions déjà choisies par le client
  useEffect(() => {
    if (building_id) {
    fetch(`${process.env.REACT_APP_API_URL}/load_chosen_solutions/${building_id}`)
        .then((response) => response.json())
        .then((data) => {
        setListChosenActions(data);
        })
        .catch((err) => setError(err.message));
    }
  }, [building_id]);

  // Lors de la récupération des solutions, on met à jour dataUsageWithActions avec les fréquences 
  // et on applique les solutions aux équipements et usages
  // On applique cette fonction qu'une seule fois au chargement de la page
  useEffect(() => {
    if (!hasExecutedOnce && listChosenActions && dataUsageWithActions && dataSolutionsEquipments && dataSolutionsUsages) {

      // Mise à jour des usages avec les solutions de type "frequency"
      const updatedDataUsageWithActions = dataUsageWithActions.map((usage) => {
        const solutionFrequency = Object.values(listChosenActions).find(
          (sol) => sol._id === usage._id && sol.type_solution === "frequency"
        );
        if (solutionFrequency) {
          return {
            ...usage,
            usage_data: {
              ...usage.usage_data,
              daily_usage: solutionFrequency.data.daily_usage,
              weekly_usage: solutionFrequency.data.weekly_usage,
              yearly_usage: solutionFrequency.data.yearly_usage,
            },
          };
        }
        return usage;
      });
      console.log('liste de solution :', listChosenActions)


      // Mise à jour des équipements avec les solutions de type "equipment"
      const updatedDataSolutionsEquipments = dataSolutionsEquipments.map((equipment) => {
        const matchingSolution = Object.values(listChosenActions).find(
          (sol) => sol.id_equipement === equipment.equipment_id && sol.name_solution === equipment.solution_name && sol.type_solution === "equipment"
        );

        if (matchingSolution) {
          const updatedEquipment = {
            ...equipment,
            solution_id : matchingSolution._id,
          };
          toggleActionEquipment(updatedEquipment, matchingSolution.data.water_savings_m3);
          return updatedEquipment;
        }
        return equipment;
      });

      // Mise à jour des équipements avec les solutions de type "usage"
      const updatedDataSolutionsUsages = dataSolutionsUsages.map((usage) => {
        const matchingSolution = Object.values(listChosenActions).find(
          (sol) => sol.data.id_usage === usage.usage_id && sol.name_solution === usage.solution_name && sol.type_solution === "usage"
        );

        if (matchingSolution) {
          const updatedUsage = {
            ...usage,
            solution_id : matchingSolution._id,
          };
          toggleActionUsage(updatedUsage, matchingSolution.data.water_savings_m3);
          return updatedUsage;
        }
        return usage;
      });

      setDataUsageWithActions(updatedDataUsageWithActions);
      setDataSolutionsEquipments(updatedDataSolutionsEquipments);
      setDataSolutionsUsages(updatedDataSolutionsUsages);
      setHasExecutedOnce(true);
    }
  }, [listChosenActions, dataUsageWithActions, dataSolutionsEquipments, dataSolutionsUsages, hasExecutedOnce]);



  // update des water_savings_m3 pour les usages et les équipements
  useEffect(() => {
    if (dataConsumptionCurrent && dataConsumptionWithActions) {
      const deltaSavings = dataConsumptionCurrent.client_summary.total_consumption - dataConsumptionWithActions.client_summary.total_consumption;

      // Calcul de la somme des water_savings_m3 existants (en excluant "None")
      const totalExistingSavings = Object.values(listChosenActions).reduce((sum, action) => {
        return sum + (typeof action.data.water_savings_m3 === "number" ? action.data.water_savings_m3 : 0);
      }, 0);
      
      const adjustedDeltaSavings = deltaSavings - totalExistingSavings;
      
      const updatedActions = Object.entries(listChosenActions).reduce(
        (acc, [key, action]) => {
          acc[key] = {
            ...action,
            data: {
              ...action.data,
              water_savings_m3:
                action.data.water_savings_m3 === "None"
                  ? adjustedDeltaSavings
                  : action.data.water_savings_m3
            }
          };
          return acc;
        },
        {}
      );
  
      setListChosenActions(updatedActions);
    }
  }, [dataConsumptionWithActions]);
  console.log("Summary", dataConsumptionWithActions)
  
  // Initialisation des selectedActions pour l'onglet Usages
  useEffect(() => {
    if (dataSolutionsUsages) {
      const initialSelectedActions = {};
      dataSolutionsUsages.forEach(usage => {
        const key = `${usage.equipment_id} | ${usage.usage_type}`;
        if (!initialSelectedActions[key]) {
          initialSelectedActions[key] = usage.solution_name;
        }
      });
      setSelectedActionUsage(initialSelectedActions);
    }
  }, [dataSolutionsUsages]);

  // Initialisation des selectedActions pour l'onglet Equipements
  useEffect(() => {
    if (dataSolutionsEquipments && dataSolutionsEquipments.length > 0) {
      const initialSelectedActions = {};
      dataSolutionsEquipments.forEach(equip => {
        const equipmentId = equip.equipment_id || "Inconnu"; // Assurez-vous d'utiliser le bon champ
        if (!initialSelectedActions[equipmentId]) {
          initialSelectedActions[equipmentId] = equip.solution_name;
        }
      });
      setSelectedActionEquipment(initialSelectedActions);
    }
  }, [dataSolutionsEquipments]);
  
  
  // Initialisation des selectedActions pour l'onglet Fréquences
  useEffect(() => {
    if (dataUsageCurrent) {
      const initialSelectedActions = {};
      Object.values(dataUsageWithActions).forEach(equip => {
        const equipmentId = equip._id_equipement || "Inconnu";
        if (!initialSelectedActions[equipmentId]) {
          initialSelectedActions[equipmentId] = equip.type_usage;
        }
      });
      setSelectedUsage(initialSelectedActions);
    }
  }, [dataUsageCurrent]);
  if (loading) return (
    <div className="centered-spinner-dashboard">
            <WalterLoader />
    </div>
  );
  if (error) return <p>Erreur : {error}</p>;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const year = date.getUTCFullYear();
    return `${day}/${month}/${year}`;
  };


return (
    <div className="building-dashboard-container">
      <div className='building-dashboard-client-header'>
        <h1 className="building-dashboard-title">{dataConsumptionCurrent?.client_summary?.client_name || "Inconnu"}</h1>
        <h1 className="building-dashboard-title-big">Plan d'action</h1>
        <button onClick={() => navigate(`/client/${client_id}/${building_id}`)}>Retour vers dashboard bâtiment</button>
      </div>

      <div 
        className='building-dashboard-header'
        style={{ backgroundImage: `url(${dataBuilding?.image_url || '/background_test.jpg'})` }}
      >
        <div className='building-dashboard-header-gradient'></div>
        <div className='building-dashboard-building-name'>{dataBuilding?.name_building || "Inconnu"}</div>
        {dataBuilding?.audit_date && (
            <div className='building-dashboard-audit-date'>Analyse réalisée le {formatDate(dataBuilding?.audit_date)}</div>
          )}
      </div>

      <div className="client-action-plan-graphs">
        <div className="client-action-plan-graph">
          <div className="client-action-plan-dashboard-item">
            <div className='client-action-plan-graph-choice'>
              <strong>Consommation d'eau sans actions par</strong>
              <select value={selectedFilter} onChange={handleFilterChange}>
                <option value="functional_unit">unité fonctionnelle</option>
                <option value="usage">usage</option>
              </select>
            </div>
            {selectedFilter === 'usage' ? (
                <WaterUsageChart data={aggregateUsageVolumes(dataConsumptionCurrent)} unit="m³" />
              ) : (
                <WaterUsageChart data={aggregateVolumeByFunctionalUnit(dataConsumptionCurrent)} unit="m³" />
              )}
          </div>
        </div>
        <div className="client-action-plan-graph">
          <div className="client-action-plan-dashboard-item">
            <div className='client-action-plan-graph-choice'>
              <strong>Consommation d'eau avec actions par</strong>
              <select value={selectedFilter} onChange={handleFilterChange}>
                  <option value="functional_unit">unité fonctionnelle</option>
                  <option value="usage">usage</option>
              </select>
            </div>
            {selectedFilter === 'usage' ? (
                <WaterUsageChart data={aggregateUsageVolumes(dataConsumptionWithActions)} unit="m³" totalInitConsumption={dataConsumptionCurrent?.client_summary?.total_consumption} />
              ) : (
                <WaterUsageChart data={aggregateVolumeByFunctionalUnit(dataConsumptionWithActions)} unit="m³" totalInitConsumption={dataConsumptionCurrent?.client_summary?.total_consumption} />
              )}
          </div>
        </div>
      </div>
      <div className="client-action-plan-tab-1-container">
        <div className="client-action-plan-tab-1-onglet">
          <button className={`client-action-plan-tab-button ${activeTab === "Usages" ? "active" : ""}`} onClick={() => setActiveTab("Usages")}>
            Usages
          </button>
          <button className={`client-action-plan-tab-button ${activeTab === "Equipements" ? "active" : ""}`} onClick={() => setActiveTab("Equipements")}>
            Équipements
          </button>
          <button className={`client-action-plan-tab-button ${activeTab === "Frequences" ? "active" : ""}`} onClick={() => setActiveTab("Frequences")}>
            Fréquences d'utilisation
          </button>
        </div>
        <div>
          {activeTab === "Usages" && (
            <table className="tab-1-table">
              <thead>
                <tr>
                  <th className="tab-1-header">Type d'équipement</th>
                  <th className="tab-1-header">Usage</th>
                  <th className="tab-1-header">Localisation</th>
                  <th className="tab-1-header-large">Action</th>
                  <th className="tab-1-header"><FaPlusCircle style={{ color: "green" }} /></th>
                  <th className="tab-1-header"><FaMinusCircle style={{ color: "red" }} /></th>
                  <th className="tab-1-header">Prix</th>
                  <th className="tab-1-header">Ampleur des travaux</th>
                  <th className="tab-1-header">Economie d'eau</th>
                  <th className="tab-1-header">Etat de l'action</th>
                </tr>
              </thead>
              <tbody>
                {dataSolutionsUsages && Object.entries(
                  dataSolutionsUsages.reduce((acc, usage) => {
                    const key = `${usage.equipment_id} | ${usage.usage_type}`;
                    if (!acc[key]) {
                      acc[key] = [];
                    }
                    acc[key].push(usage);
                    return acc;
                  }, {})
                ).map(([key, groupedUsages]) => {
                  const [equipmentId, usageType] = key.split(" | ");
                  return (
                    <tr key={key}>
                      <td className="tab-1-cell">
                        {groupedUsages[0].equipment_type === 'machine' ? (
                          <div>{translations[groupedUsages[0].machine_type] || "N/A"}</div>
                        ) : (
                          translations[groupedUsages[0].equipment_type]
                        )}
                      </td>
                      <td className="tab-1-cell">{usageType}</td>
                      <td className="tab-1-cell">
                        {groupedUsages[0].location.name_section || "N/A"}<br />
                        {groupedUsages[0].location.name_room || "N/A"}
                      </td>
                      <td className="tab-1-cell-large">
                        <select
                          onChange={(e) => handleActionChangeUsage(key, e.target.value)}
                          value={selectedActionUsage[key] || groupedUsages[0].solution_name}
                          disabled={groupedUsages.length === 1}
                        >
                          {groupedUsages.map((usage, index) => (
                            <option key={index} value={usage.solution_name}>
                              {usage.solution_name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="tab-1-cell">/</td>
                      <td className="tab-1-cell">/</td>
                      <td className="tab-1-cell">
                        {selectedActionUsage[key]
                          ? `${groupedUsages.find(u => u.solution_name === selectedActionUsage[key])?.price || "0"} €`
                          : `${groupedUsages[0]?.price || "0"} €`}
                      </td>
                      <td className="tab-1-cell">
                        {selectedActionUsage[key]
                          ? `${groupedUsages.find(u => u.solution_name === selectedActionUsage[key])?.scope_of_works || "N/A"}`
                          : `${groupedUsages[0]?.scope_of_works || "N/A"}`}
                      </td>
                      <td className="tab-1-cell">
                        {selectedActionUsage[key]
                          ? `${groupedUsages.find(u => u.solution_name === selectedActionUsage[key])?.water_savings || "N/A"} ${groupedUsages.find(u => u.solution_name === selectedActionUsage[key])?.water_savings_unit || "N/A"}`
                          : `${groupedUsages[0]?.water_savings || "N/A"} ${groupedUsages[0]?.water_savings_unit || "N/A"}`}
                      </td>
                      <td className="tab-1-cell">
                        <div className="toggle-container">
                          <div
                            className={`toggle-switch ${groupedUsages.find(u => u.solution_name === selectedActionUsage[key])?.is_solution_active ? "active" : ""}`}
                            onClick={() => {
                              const selectedUsage = groupedUsages.find(u => u.solution_name === selectedActionUsage[key]);
                              toggleActionUsage(selectedUsage);
                            }}
                          >
                            <div className="toggle-slider"></div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {activeTab === "Equipements" && (
            <table className="tab-1-table">
              <thead>
                <tr>
                  <th className="tab-1-header">Type d'équipement</th>
                  <th className="tab-1-header">Localisation</th>
                  <th className="tab-1-header-large">Action</th>
                  <th className="tab-1-header"><FaPlusCircle style={{ color: "green" }} /></th>
                  <th className="tab-1-header"><FaMinusCircle style={{ color: "red" }} /></th>
                  <th className="tab-1-header">Prix</th>
                  <th className="tab-1-header">Ampleur des travaux</th>
                  <th className="tab-1-header">Economie d'eau</th>
                  <th className="tab-1-header">Etat de l'action</th>
                </tr>
              </thead>
              <tbody>
                {dataSolutionsEquipments && Object.entries(dataSolutionsEquipments).length > 0 ? (
                  Object.entries(
                    dataSolutionsEquipments.reduce((acc, equip) => {
                      const equipmentId = equip.equipment_id || "Inconnu";
                      if (!acc[equipmentId]) {
                        acc[equipmentId] = [];
                      }
                      acc[equipmentId].push(equip);
                      return acc;
                    }, {})
                  ).map(([equipmentId, groupedEquipments]) => (
                    <tr key={equipmentId}>
                      <td className="tab-1-cell">
                        {groupedEquipments[0].equipment_type === 'machine' ? (
                          <div>{translations[groupedEquipments[0].machine_type] || "N/A"}</div>
                        ) : (
                          translations[groupedEquipments[0].equipment_type] || "N/A"
                        )}
                      </td>
                      <td className="tab-1-cell">
                        {groupedEquipments[0].location.name_section || "N/A"} <br />
                        {groupedEquipments[0].location.name_room || "N/A"}
                      </td>
                      <td className="tab-1-cell-large">
                        <select
                          onChange={(e) => handleActionChangeEquipment(equipmentId, e.target.value)}
                          value={selectedActionEquipment[equipmentId] ?? ""}
                        >
                          {groupedEquipments.map((equip, index) => (
                            <option key={index} value={equip.solution_name}>
                              {equip.solution_name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="tab-1-cell">/</td>
                      <td className="tab-1-cell">/</td>
                      <td className="tab-1-cell">
                        {selectedActionEquipment[equipmentId]
                          ? `${groupedEquipments.find(equip => equip.solution_name === selectedActionEquipment[equipmentId])?.price || "N/A"} €`
                          : `${groupedEquipments[0]?.price || "N/A"} €`}
                      </td>
                      <td className="tab-1-cell">
                        {selectedActionEquipment[equipmentId]
                          ? `${groupedEquipments.find(equip => equip.solution_name === selectedActionEquipment[equipmentId])?.scope_of_works || "N/A"}`
                          : `${groupedEquipments[0]?.scope_of_works || "N/A"}`}
                      </td>
                      <td className="tab-1-cell">
                        {selectedActionEquipment[equipmentId]
                          ? `${groupedEquipments.find(equip => equip.solution_name === selectedActionEquipment[equipmentId])?.water_savings || "N/A"} ${groupedEquipments.find(equip => equip.solution_name === selectedActionEquipment[equipmentId])?.water_savings_unit || "N/A"}`
                          : `${groupedEquipments[0]?.water_savings || "N/A"} ${groupedEquipments[0]?.water_savings_unit || "N/A"}`}
                      </td>
                      <td className="tab-1-cell">
                        <div className="toggle-container">
                          <div
                            className={`toggle-switch ${groupedEquipments.find(equip => equip.solution_name === selectedActionEquipment[equipmentId])?.is_solution_active ? "active" : ""}`}
                            onClick={() => {
                              const selectedEquip = groupedEquipments.find(
                                equip => equip.solution_name === (selectedActionEquipment[equipmentId] || groupedEquipments[0]?.solution_name)
                              );
                              toggleActionEquipment(selectedEquip);
                            }}                          
                          >
                            <div className="toggle-slider"></div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10">Aucune solution liée aux équipements disponible</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
          {activeTab === "Frequences" && (
          <table className="tab-1-table">
            <thead>
              <tr>
                <th className="tab-1-header">Type d'équipement</th>
                <th className="tab-1-header">Unité fonctionnelle</th>
                <th className="tab-1-header-large">Usage</th>
                <th className="tab-1-header">Utilisation quotidienne (min/jour ou cycle/jour)</th>
                <th className="tab-1-header">Utilisation hebdomadaire (j/sem)</th>
                <th className="tab-1-header">Utilisation annuelle (sem/an)</th>
              </tr>
            </thead>
            <tbody>
              {dataUsageWithActions && Object.entries(dataUsageWithActions).length > 0 ? (
                Object.entries(
                  Object.entries(dataUsageWithActions).reduce((acc, [key, equip]) => {
                    const equipmentId = equip._id_equipement || "Inconnu";
                    if (!acc[equipmentId]) {
                      acc[equipmentId] = [];
                    }
                    acc[equipmentId].push(equip);
                    return acc;
                  }, {})
                ).map(([equipmentId, groupedEquipments]) => (
                  <tr key={equipmentId}>
                    <td className="tab-1-cell">
                      {groupedEquipments[0].equipment_type === 'machine' ? (
                      <div>{translations[groupedEquipments[0].machine_type] || "N/A"}</div>
                      ) : (
                      translations[groupedEquipments[0].equipment_type]
                      )}
                    </td>
                    <td className="tab-1-cell">
                      {resolveFunctionalUnitLabel(groupedEquipments[0].functional_unit)}
                    </td>
                    <td className="tab-1-cell-large">
                      <select
                        onChange={(e) => handleUsageChange(equipmentId, e.target.value)}
                        value={selectedUsage[equipmentId] || groupedEquipments[0].type_usage} // Première option choisie par défaut
                        disabled={groupedEquipments.length === 1}
                      >
                        {groupedEquipments.map((equip, index) => (
                          <option key={index} value={equip.type_usage}>
                            {equip.type_usage}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="tab-1-cell">
                      <div className="client-action-plan-counter-container">
                      <button
                        className="client-action-plan-counter-button"
                        onClick={() => handleDecrement(groupedEquipments.find(equip => equip.type_usage === selectedUsage[equipmentId]), equipmentId, "daily_usage")}
                      >
                        -
                      </button>
                        <span className="client-action-plan-counter-value">
                        {selectedUsage[equipmentId]
                        ? `${groupedEquipments.find(equip => equip.type_usage === selectedUsage[equipmentId])?.usage_data?.daily_usage || 0}`
                        : `${groupedEquipments[0]?.usage_data?.daily_usage || 0}`}
                        </span>
                        <button
                          className="client-action-plan-counter-button"
                          onClick={() => handleIncrement(groupedEquipments.find(equip => equip.type_usage === selectedUsage[equipmentId]), equipmentId, "daily_usage")}
                        >
                          +
                        </button>
                      </div>
                    </td>
                    <td className="tab-1-cell">
                      <div className="client-action-plan-counter-container">
                      <button
                        className="client-action-plan-counter-button"
                        onClick={() => handleDecrement(groupedEquipments.find(equip => equip.type_usage === selectedUsage[equipmentId]), equipmentId, "weekly_usage")}
                      >
                        -
                      </button>
                        <span className="client-action-plan-counter-value">
                        {selectedUsage[equipmentId]
                        ? `${groupedEquipments.find(equip => equip.type_usage === selectedUsage[equipmentId])?.usage_data?.weekly_usage || 0}`
                        : `${groupedEquipments[0]?.usage_data?.weekly_usage || 0}`}
                        </span>
                        <button
                          className="client-action-plan-counter-button"
                          onClick={() => handleIncrement(groupedEquipments.find(equip => equip.type_usage === selectedUsage[equipmentId]), equipmentId, "weekly_usage")}
                        >
                          +
                        </button>
                      </div>
                    </td>
                    <td className="tab-1-cell">
                      <div className="client-action-plan-counter-container">
                        <button
                          className="client-action-plan-counter-button"
                          onClick={() => handleDecrement(groupedEquipments.find(equip => equip.type_usage === selectedUsage[equipmentId]), equipmentId, "yearly_usage")}
                        >
                          -
                        </button>
                        <span className="client-action-plan-counter-value">
                        {selectedUsage[equipmentId]
                        ? `${groupedEquipments.find(equip => equip.type_usage === selectedUsage[equipmentId])?.usage_data?.yearly_usage || 0}`
                        : `${groupedEquipments[0]?.usage_data?.yearly_usage || 0}`}
                        </span>
                        <button
                          className="client-action-plan-counter-button"
                          onClick={() => handleIncrement(groupedEquipments.find(equip => equip.type_usage === selectedUsage[equipmentId]), equipmentId, "yearly_usage")}
                        >
                          +
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10">Aucune donnée disponible</td>
                </tr>
              )}
            </tbody>
          </table>
          )}
        </div>
      </div>
      {/* Tableau des actions choisies */}
      <div className="client-action-plan-tab-1-container">
        <div className="client-action-plan-title-and-price">
          <h2>Actions sélectionnées</h2>
          <span className="client-action-plan-total-price">Dépenses totales : {totalPrice} €</span>
        </div>
        <table className="tab-1-table">
          <thead>
            <tr>
              <th className="tab-1-header">Type d'équipement</th>
              <th className="tab-1-header">Action</th>
              <th className="tab-1-header">Prix (€)</th>
              <th className="tab-1-header">Ampleur des travaux</th>
              <th className="tab-1-header">Économie d'eau (m³)</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(listChosenActions).length > 0 ? (
              Object.entries(listChosenActions).map(([key, action]) => (
                <tr key={key}>
                  <td className="tab-1-cell">
                    {action.data.equipment_type === 'machine' ? (
                      <div>{translations[action.data.machine_type] || "N/A"}</div>
                    ) : (
                      translations[action.data.equipment_type] || "Inconnu"
                    )}
                  </td>
                  <td className="tab-1-cell">{action.name_solution}</td>
                  <td className="tab-1-cell">{action.data.price} €</td>
                  <td className="tab-1-cell">{action.data.scope_of_works}</td>
                  <td className="tab-1-cell">{action.data.water_savings_m3 !== "None" ? action.data.water_savings_m3.toFixed(0) : "N/A"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5">Aucune action sélectionnée</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="client-action-plan-submit-container">
        <button className="client-action-plan-submit-button" onClick={handleSubmitActions}>
          Sauvegarder les actions sélectionnées
        </button>
        <button
          className="client-action-plan-submit-button"
          onClick={() => window.open('https://res.cloudinary.com/dc1felxem/image/upload/v1742892550/walter_image/xhkzrtaombiu2k5yzs1d.pdf', '_blank')}
        >
          Télécharger le rapport
        </button>
      </div>

    </div>
  );
};


export default ClientActionPlan